<template>
    <div
        v-if="products.length > 0"
        class="related-products"
    >
        <h3 class="title">
            {{ t('catalogue.discover_more') }}
        </h3>

        <div class="related-products-list">
            <ProductCard
                v-for="(product, index) in products"
                :key="index"
                :item="product"
                :variations="getVariations(product)"
                class="product-card"
                @link-clicked="scrollIntoView"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import type { ProductCardFragment, RelatedProductFragment } from '@/graphql/api'
import { useScrollIntoView } from '@/views/product/composables/ui/useScrollIntoView'
import ProductCard from '@/components/layout/cards/ProductCard.vue'
import { assertNever } from '@/helpers'

const { t } = useI18n()

export interface Props {
    products: RelatedProductFragment[]
}

defineProps<Props>()

const getVariations = (
    item: RelatedProductFragment
): ProductCardFragment[] | undefined => {
    switch (item.__typename) {
        case 'CatalogueEngagementRing':
            return item.variationsByPrimaryMetal
        case 'CatalogueWeddingRing':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
        // fallthrough
        case 'CatalogueJewelleryRing':
            return item.variations
        case 'CatalogueReadyToGoRing':
            return undefined
        default:
            return assertNever(item, undefined)
    }
}

const { scrollIntoView } = useScrollIntoView('.main', {
    top: 300,
    left: 0,
})
</script>

<style lang="scss" scoped>
.related-products {
    width: 1440px;
    max-width: 100%;
    margin: 40px auto;

    .title {
        text-align: center;
        padding: 0 0 40px;
        font-size: 1.5rem;
        color: $blue;
        font-family: $parcel-font-stack;
    }

    .related-products-list {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        padding: 20px 10px;
        width: 100%;
        max-width: 480px;
        margin: 0 auto;

        @include min-width(768) {
            max-width: initial;
        }

        @include min-width(1024) {
            flex-wrap: nowrap;
        }

        .product-card {
            width: 100%;
            height: auto;
            align-self: stretch;
            position: relative;
            background: $white;
            z-index: 2;
            padding: 0 10px;
            margin: 0 0 20px;

            @include min-width(768) {
                width: 50%;
                margin: 0;
            }

            @include min-width(1024) {
                width: 25%;
            }
        }
    }
}
</style>
